import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { AgencyCampaignSection, AgencyEditorMode } from '../../../../agency/model';
import { SupplyTab } from '../../supplyRouteSearch';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Edit3, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useTailoredSuppliesQuery } from '../../hooks';
import { formatSupplyDate, supplyPageSizes, type TailoredSupply } from '../../model';
import { useTailoredSupplyRouteFilters } from '../../useSupplyRouteFilters';

export function TailoredSupplyTable() {
  const scope = useAgencyCampaignContext();
  const { can } = useShigoPermissions();
  const canWrite = can(ShigoPermission.MadBuySupplyWrite);
  const { filter, setSearch, setPagination } = useTailoredSupplyRouteFilters();
  const query = useTailoredSuppliesQuery(filter);
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<TailoredSupply>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.name'),
          headerClassName: 'pl-4',
          className: 'pl-4 font-medium',
          value: (item) => item.name,
        },
        {
          id: 'apps',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.apps'),
          value: (item) => String(item.appsCount ?? 0),
        },
        {
          id: 'domains',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.domains'),
          value: (item) => String(item.domainsCount ?? 0),
        },
        {
          id: 'created',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('supply.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (item) => formatSupplyDate(item.createdOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canWrite,
          cell: (item) => <RowActions item={item} />,
        },
      ]),
    [canWrite],
  );
  return (
    <DashboardDataTable
      title={dashboardT('supply.tailored.title')}
      tableLabel={dashboardT('supply.tailored.title')}
      columns={columns}
      items={query.data?.content ?? []}
      getRowKey={(item) => item.id}
      loading={query.isLoading}
      errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
      emptyMessage={dashboardT('supply.tailored.empty')}
      minWidthClassName="min-w-[720px]"
      toolbar={
        <>
          <DashboardTableSearchInput
            aria-label={dashboardT('supply.search')}
            value={filter.searchTerm}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          {canWrite ? (
            <Button asChild>
              <Link
                to={
                  scope
                    ? '/a/$agencyId/campaigns/$section'
                    : '/campaigns/supply/tailored-supply/create'
                }
                params={{ agencyId: scope?.agency.id ?? '', section: AgencyCampaignSection.Supply }}
                search={
                  scope ? { supplyTab: SupplyTab.Tailored, editor: AgencyEditorMode.Create } : {}
                }
              >
                <Plus className="h-4 w-4" />
                {dashboardT('supply.tailored.create')}
              </Link>
            </Button>
          ) : null}
        </>
      }
      pagination={{
        value: {
          page: filter.page,
          pageSize: filter.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: supplyPageSizes,
        disabled: query.isLoading,
        onChange: (next) => {
          setPagination(next.page, next.pageSize);
        },
      }}
    />
  );
}

function RowActions({ item }: { item: TailoredSupply }) {
  const scope = useAgencyCampaignContext();
  return (
    <DashboardTableRowActions label={dashboardT('supply.actions')}>
      <DashboardTableRowActionItem asChild>
        <Link
          to={
            scope
              ? '/a/$agencyId/campaigns/$section'
              : '/campaigns/supply/tailored-supply/edit/$tailoredSupplyId'
          }
          params={{
            tailoredSupplyId: item.id,
            agencyId: scope?.agency.id ?? '',
            section: AgencyCampaignSection.Supply,
          }}
          search={
            scope
              ? { supplyTab: SupplyTab.Tailored, editor: AgencyEditorMode.Edit, id: item.id }
              : {}
          }
        >
          <Edit3 className="h-4 w-4" />
          {dashboardT('supply.edit')}
        </Link>
      </DashboardTableRowActionItem>
    </DashboardTableRowActions>
  );
}
